import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import {
  getStoredPermissions,
  setStoredPermissions,
  clearStoredPermissions,
  can as canHelper,
} from '@/auth/permissions'

Vue.use(Vuex)

const ACTIVE_ORGANISATION_KEY = 'active_organisation_id'
const ACTIVE_ORGANISATION_NAME_KEY = 'active_organisation_name'
const ORGANISATIONS_CACHE_KEY = 'organisations_cache'

// Module-scoped in-flight promise for fetchOrganisations dedup.
// See the action below for the full rationale.
let fetchOrganisationsInFlight = null

function toInteger(value) {
  const parsed = Number.parseInt(value, 10)
  return Number.isInteger(parsed) ? parsed : null
}

function getOrganisationIdFromStoredUser() {
  const rawUser = localStorage.getItem('auth_user')

  if (!rawUser) {
    return null
  }

  try {
    const user = JSON.parse(rawUser)
    return toInteger(user.organisation_id)
  } catch (error) {
    return null
  }
}

function getOrganisationNameFromStoredUser() {
  const rawUser = localStorage.getItem('auth_user')

  if (!rawUser) {
    return ''
  }

  try {
    const user = JSON.parse(rawUser)
    const candidates = [
      user?.organisation_name,
      user?.organisation,
      user?.organisation?.name,
      user?.organisation?.title,
      user?.organisation?.label,
    ]

    const label = candidates.find((value) => typeof value === 'string' && value.trim().length > 0)
    return label ? label.trim() : ''
  } catch (error) {
    return ''
  }
}

function getInitialOrganisationId() {
  return toInteger(localStorage.getItem(ACTIVE_ORGANISATION_KEY)) || getOrganisationIdFromStoredUser()
}

function getInitialOrganisationName() {
  const storedName = localStorage.getItem(ACTIVE_ORGANISATION_NAME_KEY)
  if (storedName && storedName.trim().length > 0) {
    return storedName.trim()
  }

  return getOrganisationNameFromStoredUser()
}

function getInitialOrganisations() {
  const cached = localStorage.getItem(ORGANISATIONS_CACHE_KEY)

  if (!cached) {
    return []
  }

  try {
    return normalizeOrganisations(JSON.parse(cached))
  } catch (error) {
    return []
  }
}

function normalizeOrganisations(organisations) {
  if (!Array.isArray(organisations) || organisations.length === 0) {
    return []
  }

  return organisations
    .map((organisation) => {
      const id = toInteger(organisation.id)

      if (!id) {
        return null
      }

      const rawLogo = organisation.logo_url || organisation.logoUrl || ''
      return {
        id,
        name: organisation.name || organisation.organisation || '',
        logo_url: typeof rawLogo === 'string' ? rawLogo.trim() : '',
      }
    })
    .filter(Boolean)
    .map((organisation) => ({
      id: organisation.id,
      name: typeof organisation.name === 'string' ? organisation.name.trim() : '',
      logo_url: organisation.logo_url,
    }))
    .filter((organisation) => organisation.name.length > 0)
}

function syncAuthUserOrganisation(normalizedId, organisationName = '') {
  const rawUser = localStorage.getItem('auth_user')

  if (!rawUser) {
    return
  }

  try {
    const user = JSON.parse(rawUser)
    user.organisation_id = normalizedId

    if (organisationName) {
      user.organisation_name = organisationName
      if (typeof user.organisation === 'string') {
        user.organisation = organisationName
      }
      if (user.organisation && typeof user.organisation === 'object') {
        user.organisation = {
          ...user.organisation,
          name: user.organisation.name || organisationName,
        }
      }
    }

    localStorage.setItem('auth_user', JSON.stringify(user))
  } catch (error) {
    // Ignore parse errors and keep app state moving.
  }
}

const state = {
  sidebarShow: 'responsive',
  sidebarMinimize: false,
  organisations: getInitialOrganisations(),
  activeOrganisationId: getInitialOrganisationId(),
  activeOrganisationName: getInitialOrganisationName(),
  organisationsLoaded: false,
  permissions: getStoredPermissions(),
}

const mutations = {
  toggleSidebarDesktop (state) {
    const sidebarOpened = [true, 'responsive'].includes(state.sidebarShow)
    state.sidebarShow = sidebarOpened ? false : 'responsive'
  },
  toggleSidebarMobile (state) {
    const sidebarClosed = [false, 'responsive'].includes(state.sidebarShow)
    state.sidebarShow = sidebarClosed ? true : 'responsive'
  },
  set (state, [variable, value]) {
    state[variable] = value
  },
  setOrganisationsLoaded(state, loaded) {
    state.organisationsLoaded = Boolean(loaded)
  },
  setOrganisations(state, organisations) {
    const normalizedOrganisations = normalizeOrganisations(organisations)
    state.organisations = normalizedOrganisations
    state.organisationsLoaded = true

    if (normalizedOrganisations.length > 0) {
      localStorage.setItem(ORGANISATIONS_CACHE_KEY, JSON.stringify(normalizedOrganisations))
    } else {
      localStorage.removeItem(ORGANISATIONS_CACHE_KEY)
    }

    const selectedOrganisation = normalizedOrganisations.find((organisation) => organisation.id === state.activeOrganisationId)
    if (selectedOrganisation) {
      state.activeOrganisationName = selectedOrganisation.name
      localStorage.setItem(ACTIVE_ORGANISATION_NAME_KEY, selectedOrganisation.name)
      syncAuthUserOrganisation(state.activeOrganisationId, selectedOrganisation.name)
      return
    }

    if (!state.activeOrganisationId && normalizedOrganisations.length > 0) {
      mutations.setActiveOrganisationContext(state, normalizedOrganisations[0])
    }
  },
  setActiveOrganisationContext(state, payload) {
    const normalizedPayload = typeof payload === 'object' && payload !== null
      ? payload
      : { id: payload }
    const normalizedId = toInteger(normalizedPayload.id)
    const normalizedName = typeof normalizedPayload.name === 'string'
      ? normalizedPayload.name.trim()
      : ''

    state.activeOrganisationId = normalizedId
    state.activeOrganisationName = normalizedName

    if (normalizedId) {
      localStorage.setItem(ACTIVE_ORGANISATION_KEY, String(normalizedId))
    } else {
      localStorage.removeItem(ACTIVE_ORGANISATION_KEY)
    }

    if (normalizedName) {
      localStorage.setItem(ACTIVE_ORGANISATION_NAME_KEY, normalizedName)
    } else {
      localStorage.removeItem(ACTIVE_ORGANISATION_NAME_KEY)
    }

    syncAuthUserOrganisation(normalizedId, normalizedName)
  },
  setActiveOrganisationId(state, organisationId) {
    const normalizedId = toInteger(organisationId)
    const matchingOrganisation = state.organisations.find((organisation) => organisation.id === normalizedId)
    mutations.setActiveOrganisationContext(state, {
      id: normalizedId,
      name: matchingOrganisation ? matchingOrganisation.name : state.activeOrganisationName,
    })
  },
  hydrateOrganisationFromAuthUser(state, user) {
    const organisationId = user ? toInteger(user.organisation_id) : null
    const organisationName = user
      ? [
        user.organisation_name,
        user.organisation,
        user.organisation?.name,
        user.organisation?.title,
        user.organisation?.label,
      ].find((value) => typeof value === 'string' && value.trim().length > 0)
      : ''

    if (!organisationId) {
      return
    }

    mutations.setActiveOrganisationContext(state, {
      id: organisationId,
      name: organisationName || state.activeOrganisationName,
    })
  },
  clearOrganisationContext(state) {
    state.activeOrganisationId = null
    state.activeOrganisationName = ''
    state.organisations = []
    state.organisationsLoaded = false
    localStorage.removeItem(ACTIVE_ORGANISATION_KEY)
    localStorage.removeItem(ACTIVE_ORGANISATION_NAME_KEY)
    localStorage.removeItem(ORGANISATIONS_CACHE_KEY)
  },
  setPermissions(state, permissions) {
    state.permissions = setStoredPermissions(permissions)
  },
  clearPermissions(state) {
    state.permissions = {}
    clearStoredPermissions()
  },
}

export default new Vuex.Store({
  state,
  mutations,
  getters: {
    activeOrganisation(state) {
      return state.organisations.find((organisation) => organisation.id === state.activeOrganisationId) || null
    },
    activeOrganisationDisplayName(state, getters) {
      if (getters.activeOrganisation && getters.activeOrganisation.name) {
        return getters.activeOrganisation.name
      }

      return state.activeOrganisationName || ''
    },
    can: (state) => (permission) => canHelper(permission, state.permissions),
  },
  actions: {
    fetchOrganisations({ commit, state }) {
      // Coalesce concurrent callers onto a single in-flight request.
      // main.js fires this on boot; TheSidebar.vue fires it again from
      // mounted() because `organisationsLoaded` is still false at that
      // point. Without dedup, every page refresh issues two parallel
      // `/api/organisations` calls per staff member — on Hostinger's
      // per-user MySQL connection cap that pressure surfaces as
      // SQLSTATE[HY000] [2002] Operation not permitted on the very
      // first query of any concurrent request (the Sanctum token
      // lookup), 500ing the whole page.
      if (fetchOrganisationsInFlight) {
        return fetchOrganisationsInFlight
      }

      const apiAdress = process.env.VUE_APP_API_URL || 'http://127.0.0.1:8000'
      commit('setOrganisationsLoaded', false)

      fetchOrganisationsInFlight = (async () => {
        try {
          const response = await axios.get(apiAdress + '/api/organisations')
          commit('setOrganisations', response.data)

          const activeStillValid = state.organisations.some((organisation) => organisation.id === state.activeOrganisationId)
          if (!activeStillValid && state.organisations.length > 0) {
            commit('setActiveOrganisationContext', state.organisations[0])
          }
        } catch (error) {
          commit('setOrganisationsLoaded', true)
          throw error
        } finally {
          fetchOrganisationsInFlight = null
        }
      })()

      return fetchOrganisationsInFlight
    }
  }
})
